"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import Image from "next/image";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowUp, Mic, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import styles from "./coach.module.scss";
import { ASK_EXAMPLES, ASK_UI, CHAT_LIVE, CHAT_MORNING, CHAT_REVIEW, CHAT_UI, type CoachMoment } from "./coachData";

/**
 * Figma "Chat · sticky" (3165:31116 and the three states in 3083:28401).
 * One continuous thread: the Ask exchange comes first, and every card the
 * user scrolls past appends its coach message below it. Scrolling back up
 * removes the later messages again, and the thread keeps itself scrolled to
 * the newest message.
 */

const EASE = [0.16, 1, 0.3, 1] as const;
const WORD_STAGGER = 0.045;

type Phase = 0 | 1 | 2 | 3; // 0 blank, 1 question shown, 2 thinking, 3 answer

function usePhase(key: string, schedule: [number, number, number]) {
  const reduced = useReducedMotion();
  const [phase, setPhase] = useState<Phase>(reduced ? 3 : 0);
  useEffect(() => {
    if (reduced) {
      setPhase(3);
      return;
    }
    setPhase(0);
    const timers = schedule.map((ms, i) => window.setTimeout(() => setPhase((i + 1) as Phase), ms));
    return () => timers.forEach((t) => window.clearTimeout(t));
    // The schedule is constant per key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, reduced]);
  return phase;
}

export function CoachAvatar({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "grid shrink-0 place-items-center overflow-hidden rounded-full bg-[#ff6509] bg-[url('/ai-features/color-1.svg')] bg-center bg-[length:157%_157%]",
        className,
      )}>
      <Image
        src="/ai-features/fox-1.svg"
        width={36}
        height={36}
        alt=""
        className="h-full w-full"
      />
    </span>
  );
}

function Words({ text, className, delay = 0 }: { text: string; className?: string; delay?: number }) {
  const reduced = useReducedMotion();
  return (
    <p className={cn("m-0", className)}>
      {text.split(" ").map((word, i) => (
        <motion.span
          key={i}
          className="inline-block whitespace-pre"
          initial={reduced ? false : { opacity: 0, y: 5, filter: "blur(4px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{
            duration: 0.45,
            ease: [...EASE],
            delay: delay + i * WORD_STAGGER,
          }}>
          {word}{" "}
        </motion.span>
      ))}
    </p>
  );
}

function CountUp({ value, duration = 900, delay = 0 }: { value: number; duration?: number; delay?: number }) {
  const reduced = useReducedMotion();
  const [shown, setShown] = useState(reduced ? value : 0);
  useEffect(() => {
    if (reduced) {
      setShown(value);
      return;
    }
    let frame = 0;
    const start = performance.now() + delay * 1000;
    const tick = (now: number) => {
      const t = Math.min(1, Math.max(0, (now - start) / duration));
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(Math.round(value * eased));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, duration, delay, reduced]);
  return <>{shown}</>;
}

function Card({ children, delay, className }: { children: ReactNode; delay: number; className?: string }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      className={cn(
        "flex w-full flex-col gap-[2rem] rounded-[0.5rem] border border-white/10 bg-[#141414] p-[2rem]",
        className,
      )}
      initial={reduced ? false : { opacity: 0, y: 10, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.6, ease: [...EASE], delay }}>
      {children}
    </motion.div>
  );
}

function Thinking() {
  return (
    <span
      className="flex h-[3rem] items-center gap-[0.5rem]"
      aria-label="TS Coach is typing">
      <i className={styles.typingDot} />
      <i className={styles.typingDot} />
      <i className={styles.typingDot} />
    </span>
  );
}

function CoachMessage({
  phase,
  title,
  titleClassName = "text-[2.25rem]",
  children,
}: {
  phase: Phase;
  title: string;
  titleClassName?: string;
  children?: ReactNode;
}) {
  return (
    <motion.div
      className="flex w-full items-start gap-[2rem]"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [...EASE] }}>
      <CoachAvatar className="h-[4.5rem] w-[4.5rem]" />
      <div className="flex min-w-0 flex-1 flex-col gap-[1.25rem]">
        <p className="m-0 font-spacemono text-[1.5rem] leading-[1.4] text-[#FF5500] uppercase">{CHAT_UI.coach}</p>
        {phase === 2 && <Thinking />}
        {phase >= 3 && (
          <>
            <Words
              text={title}
              className={cn("leading-[1.5] text-white", titleClassName)}
            />
            {children}
          </>
        )}
      </div>
    </motion.div>
  );
}

function AskExchange({ index, askedAt }: { index: number; askedAt: number }) {
  const example = ASK_EXAMPLES[index] ?? ASK_EXAMPLES[0];
  // When the Ask stage is still typing this question, wait for it to "send";
  // when the chat merely scrolled back into view, answer right away.
  const [typingMs] = useState(() => {
    if (!askedAt) return 0;
    const elapsed = Date.now() - askedAt;
    const total = 760 + (Array.from(example.question).length / 32) * 1000 + 800;
    return Math.max(0, total - elapsed);
  });
  const phase = usePhase(`ask-${index}-${askedAt}`, [typingMs, typingMs + 500, typingMs + 1500]);
  return (
    <>
      {phase >= 1 && (
        <motion.div
          className="flex w-full flex-col items-end gap-[0.75rem]"
          initial={{ opacity: 0, y: 10, x: 12 }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          transition={{ duration: 0.5, ease: [...EASE] }}>
          <span className="font-spacemono text-[1.5rem] leading-[1.4] text-white/40 uppercase">{CHAT_UI.you}</span>
          <span className="max-w-full rounded-[1.5rem] bg-white/10 px-[2.25rem] py-[1.5rem] text-[2rem] leading-[1.5] text-white">
            {example.question}
          </span>
        </motion.div>
      )}
      {phase >= 2 && (
        <CoachMessage
          phase={phase}
          title={example.answer}
          titleClassName="text-[2rem]"
        />
      )}
    </>
  );
}

function PartsRow({ parts, numeric }: { parts: { label: string; value: string | number }[]; numeric?: boolean }) {
  return (
    <div className="flex w-full items-start justify-between gap-[1rem]">
      {parts.map((part, i) => (
        <span
          key={part.label}
          className="flex flex-col gap-[0.25rem]">
          <span className="text-[2rem] leading-[1.5] text-white tabular-nums">
            {numeric && typeof part.value === "number" ? (
              <CountUp
                value={part.value}
                delay={0.2 + i * 0.08}
              />
            ) : (
              part.value
            )}
          </span>
          <span className="text-[1.5rem] leading-[1.5] text-white/50">{part.label}</span>
        </span>
      ))}
    </div>
  );
}

function MorningExchange() {
  const phase = usePhase("morning", [0, 250, 950]);
  const delay = CHAT_MORNING.title.split(" ").length * WORD_STAGGER + 0.25;
  return (
    <CoachMessage
      phase={phase}
      title={CHAT_MORNING.title}>
      <Card delay={delay}>
        <span className="text-[1.75rem] leading-[1.5] text-white/60">{CHAT_MORNING.cardLabel}</span>
        <span className="flex items-baseline gap-[1.25rem]">
          <span className="text-[6rem] leading-[1.5] text-white tabular-nums">
            <CountUp
              value={CHAT_MORNING.score}
              duration={1100}
              delay={delay + 0.1}
            />
          </span>
          <motion.span
            className="text-[2rem] leading-[1.5] text-[#2ecc71]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: delay + 0.9, duration: 0.4 }}>
            {CHAT_MORNING.scoreLabel}
          </motion.span>
        </span>
        <PartsRow
          parts={CHAT_MORNING.parts}
          numeric
        />
      </Card>
    </CoachMessage>
  );
}

function LiveExchange() {
  const phase = usePhase("live", [0, 250, 950]);
  const delay = CHAT_LIVE.title.split(" ").length * WORD_STAGGER + 0.25;
  return (
    <CoachMessage
      phase={phase}
      title={CHAT_LIVE.title}>
      <Card delay={delay}>
        <span className="flex flex-col gap-[0.5rem] text-[1.75rem] leading-[1.5] text-white/60">
          <span>{CHAT_LIVE.alert}</span>
          <span className="opacity-50">{CHAT_LIVE.time}</span>
        </span>
        <span className={cn("flex gap-[1rem] text-[1.45rem] font-medium leading-none", styles.chatGeist)}>
          <motion.span
            className="rounded-[0.8rem] bg-white px-[1.25rem] py-[1.25rem] text-black"
            initial={{ scale: 1 }}
            animate={{ scale: [1, 0.94, 1] }}
            transition={{ delay: delay + 1.2, duration: 0.5, ease: "easeOut" }}>
            {CHAT_LIVE.actions[0]}
          </motion.span>
          <span className="rounded-[0.8rem] border border-white/10 bg-[#0a0a0a] px-[1.25rem] py-[1.25rem] text-white/60">
            {CHAT_LIVE.actions[1]}
          </span>
        </span>
      </Card>
    </CoachMessage>
  );
}

function ReviewExchange() {
  const phase = usePhase("review", [0, 250, 950]);
  const delay = CHAT_REVIEW.title.split(" ").length * WORD_STAGGER + 0.25;
  return (
    <CoachMessage
      phase={phase}
      title={CHAT_REVIEW.title}>
      <Card delay={delay}>
        <span className="text-[1.75rem] leading-[1.5] text-white/60">{CHAT_REVIEW.cardLabel}</span>
        <PartsRow parts={CHAT_REVIEW.parts} />
        <motion.span
          className="text-[1.75rem] leading-[1.5] text-white/60 opacity-60"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          transition={{ delay: delay + 0.5, duration: 0.5 }}>
          {CHAT_REVIEW.footer}
        </motion.span>
      </Card>
    </CoachMessage>
  );
}

const EXCHANGES: Record<Exclude<CoachMoment, "ask">, () => JSX.Element> = {
  morning: MorningExchange,
  live: LiveExchange,
  review: ReviewExchange,
};

export default function CoachChat({
  moments,
  activeIndex,
  askIndex,
  askedAt,
  className,
}: {
  /** Card order; the first one is the Ask card. */
  moments: CoachMoment[];
  /** Index of the active card; messages for cards 0..activeIndex are shown. */
  activeIndex: number;
  askIndex: number;
  /** Timestamp of the last Ask selection, 0 before the first one. */
  askedAt: number;
  className?: string;
}) {
  const thread = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const shown = moments.slice(0, Math.max(0, activeIndex) + 1);

  // Keep the newest message in view as messages stream in or get appended.
  useEffect(() => {
    const node = thread.current;
    if (!node) return;
    const toBottom = () =>
      node.scrollTo({
        top: node.scrollHeight,
        behavior: reduced ? "auto" : "smooth",
      });
    const observer = new ResizeObserver(toBottom);
    observer.observe(node.firstElementChild ?? node);
    toBottom();
    return () => observer.disconnect();
  }, [reduced]);

  return (
    <div
      className={cn("flex flex-col overflow-hidden border border-white/[15%] bg-[#0a0a0a] text-white", className)}
      role="group"
      aria-label="TS Coach conversation preview">
      {/* Bar */}
      <div className="flex shrink-0 items-center gap-[1.25rem] border-b border-white/[0.08] px-[2.5rem] py-[1.75rem] whitespace-nowrap">
        <CoachAvatar className="h-[3.5rem] w-[3.5rem]" />
        <span className="text-[2rem] leading-[1.5] font-medium">{CHAT_UI.name}</span>
        <span className="rounded-full bg-[#1f3a2b] px-[1rem] py-[0.25rem] text-[1.5rem] leading-[1.5] text-[#7ee2a8]">
          {CHAT_UI.beta}
        </span>
        <span className="truncate text-[1.75rem] leading-[1.5] text-white/45">{CHAT_UI.powered}</span>
      </div>

      {/* Thread */}
      <div
        ref={thread}
        className={cn("relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden", styles.thread)}>
        <div className="flex min-h-full w-full flex-col justify-end gap-[3rem] p-[2.5rem]">
          <AnimatePresence initial={false}>
            {shown.map((moment, i) => {
              const key = moment === "ask" ? `ask-${askIndex}-${askedAt}` : moment;
              const Exchange = moment === "ask" ? null : EXCHANGES[moment];
              return (
                <motion.div
                  key={key}
                  className="flex w-full flex-col gap-[3rem]"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{
                    opacity: 0,
                    y: -10,
                    height: 0,
                    marginTop: "-3rem",
                    transition: { duration: 0.28, ease: [0.4, 0, 1, 1] },
                  }}
                  transition={{ duration: 0.4, ease: [...EASE] }}>
                  {moment === "ask" ? (
                    <AskExchange
                      index={askIndex}
                      askedAt={askedAt}
                    />
                  ) : (
                    Exchange && <Exchange key={i} />
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      {/* Input */}
      <div className="flex shrink-0 flex-col items-center gap-[2.35rem] px-[2rem] pt-[1rem] pb-[2rem]">
        <div
          className="flex w-full items-center justify-between rounded-full border border-white/10 bg-[#0a0a0a] py-[1.57rem] pr-[1.57rem] pl-[4.18rem]"
          aria-hidden="true">
          <span className="flex min-w-0 items-center gap-[1.25rem]">
            <Plus className="h-[3.1rem] w-[3.1rem] shrink-0" />
            <span className={cn("truncate text-[1.75rem] leading-none text-white/40", styles.chatGeist)}>
              {ASK_UI.placeholder}
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-[3.25rem]">
            <Mic className="h-[3.1rem] w-[3.1rem]" />
            <span className="grid h-[5.5rem] w-[5.5rem] place-items-center rounded-full bg-[#FF5500]">
              <ArrowUp className="h-[3.1rem] w-[3.1rem]" />
            </span>
          </span>
        </div>
        <p className={cn("m-0 w-full text-center text-[1.83rem] leading-[1.35] text-white/30", styles.chatGeist)}>
          {ASK_UI.disclaimer}
        </p>
      </div>
    </div>
  );
}
